import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';

const FullPageContainer = styled.div`
  min-height: 100vh;
  background: ${(props) => props.theme.colors.background.primary};
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.fonts.primary};
`;

const HeroSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
  gap: 0;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  background: ${(props) => props.theme.colors.background.secondary};
  padding: 80px 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 60px 30px;
  }
`;

const RightPanel = styled.div`
  background: ${(props) => props.theme.colors.background.dark};
  padding: 80px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 60px 30px;
  }
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 16px;
  line-height: 1.2;
  color: ${(props) => props.theme.colors.text.primary};

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Highlight = styled.span`
  color: ${(props) => props.theme.colors.primary};
`;

const Subtitle = styled.p`
  font-size: 18px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.text.secondary};
  margin-bottom: 48px;
  line-height: 1.6;
`;

const StepsList = styled.div`
  margin-bottom: 48px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 20px;
  padding: 20px;
  background: ${(props) => props.theme.colors.primaryOpacity(0.05)};
  border-left: 3px solid ${(props) => props.theme.colors.primary};
  border-radius: ${(props) => props.theme.borderRadius.md};
  transition: ${(props) => props.theme.transitions.normal};

  &:hover {
    background: ${(props) => props.theme.colors.primaryOpacity(0.1)};
  }
`;

const StepIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${(props) => props.theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  flex-shrink: 0;
  color: #ffffff;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
  color: ${(props) => props.theme.colors.text.primary};
`;

const StepDescription = styled.p`
  color: ${(props) => props.theme.colors.text.secondary};
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`;

const VideoContainer = styled.div`
  width: 100%;
  max-width: 600px;
  aspect-ratio: 16/9;
  border-radius: ${(props) => props.theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${(props) => props.theme.shadows.xl};
  margin-bottom: 32px;
  position: relative;
  background: ${(props) => props.theme.colors.background.dark};

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ContinueButton = styled.button`
  padding: 12px 24px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.1)'
      : 'rgba(250, 70, 22, 0.05)'};
  border: 2px solid ${(props) => props.theme?.colors?.primary || '#fa4616'};
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  font-family: ${(props) => props.theme.fonts.primary};

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.2)'
        : 'rgba(250, 70, 22, 0.1)'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PricingEducatorLanding = () => {
  const history = useHistory();
  const location = useLocation();

  // Determine route prefix based on current path
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const steps = [
    {
      number: '1',
      title: 'Understand the New Laws',
      description: 'Get clear on what all-in pricing means for your business',
    },
    {
      number: '2',
      title: 'Learn the Framework',
      description: 'See how transparent pricing actually builds customer trust',
    },
    {
      number: '3',
      title: 'Refund or Credit Policies',
      description:
        'Give customers confidence to buy early and lock in sales with clear policies',
    },
    {
      number: '4',
      title: 'Calculate Your Revenue',
      description:
        'Interactive tool shows real numbers for your specific scenario',
    },
  ];

  const handleContinue = () => {
    history.push(`${routePrefix}/pricing-educator/all-in-pricing-coming`);
  };

  return (
    <EnhancedThemeProvider>
      <FullPageContainer>
        <HeroSection>
          <LeftPanel>
            <Title>
              Turn <Highlight>All-In Pricing</Highlight> Into Your Competitive
              Advantage
            </Title>
            <Subtitle>
              The ticketing industry is changing. Don't let all-in pricing hurt
              your bottom line. Learn how to use dynamic pricing models to
              maintain—and even grow—your revenue.
            </Subtitle>

            <StepsList>
              {steps.map((step) => (
                <StepItem key={step.number}>
                  <StepIcon>{step.number}</StepIcon>
                  <StepContent>
                    <StepTitle>{step.title}</StepTitle>
                    <StepDescription>{step.description}</StepDescription>
                  </StepContent>
                </StepItem>
              ))}
            </StepsList>
          </LeftPanel>

          <RightPanel>
            <VideoContainer>
              <iframe
                src="https://player.vimeo.com/video/1118728475?badge=0&autopause=0&player_id=0&app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                title="Pricing Educator Introduction"
              />
            </VideoContainer>
            <ContinueButton onClick={handleContinue}>
              Begin Your Journey
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </ContinueButton>
          </RightPanel>
        </HeroSection>
      </FullPageContainer>
    </EnhancedThemeProvider>
  );
};

export default PricingEducatorLanding;
